import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest, apiUpload } from '@/lib/api-client'

export interface LocalizedText {
  zh: string
  en: string
}

export interface PublicEventSummary {
  id: string
  slug: string
  eventType: string
  title: LocalizedText
  summary: LocalizedText
  startsAt: string
  endsAt: string
  timezone: string
  location: LocalizedText
  organizerName: string
  officialUrl: string | null
  registrationUrl: string | null
  enabledModules: string[]
}

export interface PublicShuttleSlot {
  slotCode: string
  serviceDate: string
  direction: 'to_venue' | 'return'
  origin: LocalizedText
  destination: LocalizedText
  departsAt: string
  arrivesAt: string
  capacity: number | null
  bookable: boolean
  availability: 'unavailable' | 'available' | 'few' | 'full'
  boardingNote: LocalizedText | null
}

export interface PublicEventDetail extends PublicEventSummary {
  shuttleSlots: PublicShuttleSlot[]
}

export function usePublicEvents(): UseQueryResult<PublicEventSummary[]> {
  return useQuery({
    queryKey: ['public-events'],
    queryFn: () => apiRequest<PublicEventSummary[]>('/api/public/events'),
  })
}

export function usePublicEvent(slug: string): UseQueryResult<PublicEventDetail> {
  return useQuery({
    queryKey: ['public-events', slug],
    queryFn: () => apiRequest<PublicEventDetail>(`/api/public/events/${encodeURIComponent(slug)}`),
    retry: false,
  })
}

export interface MyShuttleReservation {
  id: string
  slotCode: string
  status: 'confirmed' | 'waitlisted'
  serviceDate: string
  direction: 'to_venue' | 'return'
  origin: LocalizedText
  destination: LocalizedText
  departsAt: string
  arrivesAt: string
  waitlistPosition: number | null
  cancellable: boolean
  credential: string | null
  seatNo?: number
  riderName?: string | null
}

const myKey = (slug: string) => ['events', slug, 'shuttle-my']

export function useMyShuttle(
  slug: string,
  enabled: boolean,
): UseQueryResult<MyShuttleReservation[]> {
  return useQuery({
    queryKey: myKey(slug),
    enabled,
    queryFn: () =>
      apiRequest<MyShuttleReservation[]>(`/api/events/${encodeURIComponent(slug)}/shuttle/my`),
  })
}

export interface ReserveInput {
  slotCode: string
  companions?: string[]
}

export function useReserveShuttle(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: ReserveInput) =>
      apiRequest<MyShuttleReservation & { party?: MyShuttleReservation[] }>(
        `/api/events/${encodeURIComponent(slug)}/shuttle/reservations`,
        {
          method: 'POST',
          body: {
            slotCode: input.slotCode,
            ...(input.companions !== undefined && input.companions.length > 0
              ? { companions: input.companions }
              : {}),
          },
          headers: { 'idempotency-key': crypto.randomUUID() },
        },
      ),
    // The POST result is the server's decision: show it at once, then re-sync in the background.
    onSuccess: (created) => {
      const party = created.party ?? [created]
      client.setQueryData<MyShuttleReservation[]>(myKey(slug), (current) => [
        ...(current ?? []).filter((r) => !party.some((p) => p.id === r.id)),
        ...party,
      ])
    },
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: myKey(slug) })
      await client.invalidateQueries({ queryKey: ['public-events', slug] })
    },
  })
}

export interface QuickBookingInput {
  email: string
  displayName: string
  slotCode: string
  companions: string[]
  turnstileToken?: string
}

/** Guest request: holds no seat; the emailed link confirms it (EV-222). */
export function useRequestQuickBooking(slug: string) {
  return useMutation({
    mutationFn: (input: QuickBookingInput) =>
      apiRequest<{ acknowledged: true }>(
        `/api/public/events/${encodeURIComponent(slug)}/shuttle/quick-booking`,
        {
          method: 'POST',
          body: {
            ...input,
            companions: input.companions.length > 0 ? input.companions : undefined,
            confirmRealName: true,
          },
        },
      ),
  })
}

export interface QuickBookingResult {
  party: MyShuttleReservation[]
  failure: string | null
}

export function useConfirmQuickBooking(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (token: string) =>
      apiRequest<QuickBookingResult>(
        `/api/public/events/${encodeURIComponent(slug)}/shuttle/quick-booking/confirm`,
        { method: 'POST', body: { token } },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['session'] })
      await client.invalidateQueries({ queryKey: myKey(slug) })
    },
  })
}

export interface RiderProfile {
  complete: boolean
  realName: string
  organization: string | null
  jobTitle: string | null
  mobile: string | null
}

export interface RiderProfileInput {
  realName: string
  organization: string
  jobTitle: string
  mobile: string
  confirmRealName: true
}

const riderKey = (slug: string) => ['events', slug, 'rider-profile']

export function useRiderProfile(slug: string, enabled: boolean): UseQueryResult<RiderProfile> {
  return useQuery({
    queryKey: riderKey(slug),
    enabled,
    queryFn: () =>
      apiRequest<RiderProfile>(`/api/events/${encodeURIComponent(slug)}/shuttle/profile`),
  })
}

export function useSaveRiderProfile(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: RiderProfileInput) =>
      apiRequest<{ complete: boolean }>(`/api/events/${encodeURIComponent(slug)}/shuttle/profile`, {
        method: 'PUT',
        body: input,
      }),
    onSuccess: async () => client.invalidateQueries({ queryKey: riderKey(slug) }),
  })
}

export function useCancelShuttle(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ id: string }>(
        `/api/events/${encodeURIComponent(slug)}/shuttle/reservations/${encodeURIComponent(id)}/cancel`,
        { method: 'POST' },
      ),
    onSettled: async () => {
      await client.invalidateQueries({ queryKey: myKey(slug) })
      await client.invalidateQueries({ queryKey: ['public-events', slug] })
    },
  })
}

const remindersKey = (slug: string) => ['events', slug, 'reminders']

export function useReminders(slug: string, enabled: boolean): UseQueryResult<{ enabled: boolean }> {
  return useQuery({
    queryKey: remindersKey(slug),
    enabled,
    queryFn: () =>
      apiRequest<{ enabled: boolean }>(`/api/events/${encodeURIComponent(slug)}/shuttle/reminders`),
  })
}

export function useSetReminders(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (value: boolean) =>
      apiRequest<{ enabled: boolean }>(
        `/api/events/${encodeURIComponent(slug)}/shuttle/reminders`,
        {
          method: 'PUT',
          body: { enabled: value },
        },
      ),
    onSuccess: (result) => client.setQueryData(remindersKey(slug), result),
  })
}

export interface CheckinResult {
  result: 'success' | 'duplicate' | 'wrong_slot' | 'invalid' | 'not_confirmed'
  displayName?: string
  slotCode?: string
}

export type EventRole = 'owner' | 'manager' | 'scanner' | 'viewer'
export type EventCapability =
  'dashboard' | 'checkin' | 'manifest' | 'notices' | 'meals' | 'tickets' | 'operations' | 'members'

export interface StaffMe {
  role: EventRole | null
  capabilities: EventCapability[]
}

export function can(me: StaffMe | undefined, capability: EventCapability): boolean {
  return me?.capabilities?.includes(capability) === true
}

export function useStaffRole(slug: string, enabled: boolean): UseQueryResult<StaffMe> {
  return useQuery({
    queryKey: ['events', slug, 'staff-me'],
    enabled,
    queryFn: () => apiRequest<StaffMe>(`/api/events/${encodeURIComponent(slug)}/staff/me`),
  })
}

export interface ManagedEvent {
  slug: string
  titleZh: string
  titleEn: string
  role: EventRole
}

/** Events the signed-in user can manage (Account Menu shortcut); empty for everyone else. */
export function useManagedEvents(enabled: boolean): UseQueryResult<ManagedEvent[]> {
  return useQuery({
    queryKey: ['events', 'staff-mine'],
    enabled,
    staleTime: 60_000,
    queryFn: () => apiRequest<ManagedEvent[]>('/api/events/staff/mine'),
  })
}

export interface EventMember {
  email: string
  role: EventRole
  status: 'active' | 'pending'
  createdAt: string
  updatedAt: string
}

export function useEventMembers(slug: string, enabled: boolean): UseQueryResult<EventMember[]> {
  return useQuery({
    queryKey: ['events', slug, 'members'],
    enabled,
    queryFn: () => apiRequest<EventMember[]>(`${base(slug)}/staff/members`),
  })
}

export function useMemberAction(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (v: { method: 'POST' | 'PATCH' | 'DELETE'; email?: string; body?: unknown }) =>
      apiRequest<unknown>(
        `${base(slug)}/staff/members${v.method === 'POST' ? '' : `/${encodeURIComponent(v.email ?? '')}`}`,
        { method: v.method, ...(v.body === undefined ? {} : { body: v.body }) },
      ),
    onSuccess: async () => client.invalidateQueries({ queryKey: ['events', slug, 'members'] }),
  })
}

export function checkinRequest(slug: string, slotCode: string, credential: string) {
  return apiRequest<CheckinResult>(`/api/events/${encodeURIComponent(slug)}/staff/checkins`, {
    method: 'POST',
    body: { slotCode, credential },
  })
}

export interface PublicNotice {
  id: string
  severity: 'info' | 'important' | 'critical'
  title: LocalizedText
  body: LocalizedText
  createdAt: string
}

export function usePublicNotices(slug: string): UseQueryResult<PublicNotice[]> {
  return useQuery({
    queryKey: ['public-events', slug, 'notices'],
    queryFn: () =>
      apiRequest<PublicNotice[]>(`/api/public/events/${encodeURIComponent(slug)}/notices`),
  })
}

export const DIETARY_CODES = [
  'none',
  'vegetarian',
  'vegan',
  'halal',
  'no_beef',
  'no_pork',
  'seafood_allergy',
  'nut_allergy',
  'other',
] as const

export interface MealsState {
  days: { serviceDate: string; attending: boolean; dietaryCode: string; note: string | null }[]
  banquet: 'yes' | 'no' | null
  deadlineAt: string | null
  open: boolean
}

export function useMyMeals(slug: string, enabled: boolean): UseQueryResult<MealsState> {
  return useQuery({
    queryKey: ['events', slug, 'meals'],
    enabled,
    queryFn: () => apiRequest<MealsState>(`/api/events/${encodeURIComponent(slug)}/meals/me`),
  })
}

export function useSaveMeals(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Pick<MealsState, 'days' | 'banquet'>) =>
      apiRequest<MealsState>(`/api/events/${encodeURIComponent(slug)}/meals/me`, {
        method: 'PUT',
        body,
      }),
    onSuccess: async () => client.invalidateQueries({ queryKey: ['events', slug, 'meals'] }),
  })
}

export interface ServiceTicket {
  id: string
  category: string
  severity: 'normal' | 'urgent'
  subject: string
  body: string
  status: 'new' | 'in_progress' | 'resolved' | 'closed'
  createdAt: string
  updatedAt: string
}

export function useMyTickets(slug: string, enabled: boolean): UseQueryResult<ServiceTicket[]> {
  return useQuery({
    queryKey: ['events', slug, 'my-tickets'],
    enabled,
    queryFn: () =>
      apiRequest<ServiceTicket[]>(`/api/events/${encodeURIComponent(slug)}/support-tickets/me`),
  })
}

export function useCreateTicket(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { category: string; severity: string; subject: string; body: string }) =>
      apiRequest<ServiceTicket>(`/api/events/${encodeURIComponent(slug)}/support-tickets`, {
        method: 'POST',
        body,
      }),
    onSuccess: async () => client.invalidateQueries({ queryKey: ['events', slug, 'my-tickets'] }),
  })
}

export interface Dashboard {
  generatedAt: string
  slots: {
    slotCode: string
    serviceDate: string
    direction: string
    capacity: number | null
    confirmed: number
    waitlisted: number
    checkedIn: number
  }[]
  meals: { serviceDate: string; dietaryCode: string; attending: number }[]
  banquet: { yes: number; no: number }
  openTickets: number
}

export function useDashboard(slug: string, enabled: boolean): UseQueryResult<Dashboard> {
  return useQuery({
    queryKey: ['events', slug, 'dashboard'],
    enabled,
    queryFn: () => apiRequest<Dashboard>(`/api/events/${encodeURIComponent(slug)}/staff/dashboard`),
  })
}

export function useStaffTickets(slug: string, enabled: boolean): UseQueryResult<ServiceTicket[]> {
  return useQuery({
    queryKey: ['events', slug, 'staff-tickets'],
    enabled,
    queryFn: () =>
      apiRequest<ServiceTicket[]>(`/api/events/${encodeURIComponent(slug)}/staff/tickets`),
  })
}

export function useSetTicketStatus(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (v: { id: string; status: ServiceTicket['status'] }) =>
      apiRequest(
        `/api/events/${encodeURIComponent(slug)}/staff/tickets/${encodeURIComponent(v.id)}`,
        {
          method: 'PATCH',
          body: { status: v.status },
        },
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['events', slug, 'staff-tickets'] })
      await client.invalidateQueries({ queryKey: ['events', slug, 'dashboard'] })
    },
  })
}

export function usePublishNotice(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiRequest(`/api/events/${encodeURIComponent(slug)}/staff/notices`, { method: 'POST', body }),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: ['public-events', slug, 'notices'] }),
  })
}

export function useWithdrawNotice(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest(
        `/api/events/${encodeURIComponent(slug)}/staff/notices/${encodeURIComponent(id)}/withdraw`,
        {
          method: 'POST',
        },
      ),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: ['public-events', slug, 'notices'] }),
  })
}

export interface BatchScan {
  offlineEventId: string
  credential: string
  scannedAt: string
}

export function batchCheckinRequest(slug: string, slotCode: string, scans: BatchScan[]) {
  return apiRequest<(CheckinResult & { offlineEventId: string; replay?: boolean })[]>(
    `/api/events/${encodeURIComponent(slug)}/staff/checkins/batch`,
    { method: 'POST', body: { slotCode, scans } },
  )
}

const base = (slug: string) => `/api/events/${encodeURIComponent(slug)}`

export interface Shift {
  id: string
  title: string
  location: string
  startsAt: string
  endsAt: string
  volunteerEmails?: string[]
}

export function useMyShifts(slug: string, enabled: boolean): UseQueryResult<Shift[]> {
  return useQuery({
    queryKey: ['events', slug, 'my-shifts'],
    enabled,
    queryFn: () => apiRequest<Shift[]>(`${base(slug)}/shifts/me`),
  })
}

export interface MyPoster {
  id: string
  locationCode: string
  title: string
  requests: { id: string; item: string; quantity: number; notes: string | null; status: string }[]
}

export function useMyPosters(slug: string, enabled: boolean): UseQueryResult<MyPoster[]> {
  return useQuery({
    queryKey: ['events', slug, 'my-posters'],
    enabled,
    queryFn: () => apiRequest<MyPoster[]>(`${base(slug)}/posters/me`),
  })
}

export function useRequestEquipment(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      allocationId: string
      item: string
      quantity: number
      notes: string | null
    }) => apiRequest(`${base(slug)}/equipment-requests`, { method: 'POST', body }),
    onSuccess: async () => client.invalidateQueries({ queryKey: ['events', slug, 'my-posters'] }),
  })
}

export interface SpeakerState {
  eligible: boolean
  maxBytes: number
  files: { id: string; filename: string; sizeBytes: number; status: string; createdAt: string }[]
}

export function useSpeakerState(slug: string, enabled: boolean): UseQueryResult<SpeakerState> {
  return useQuery({
    queryKey: ['events', slug, 'speaker'],
    enabled,
    queryFn: () => apiRequest<SpeakerState>(`${base(slug)}/speaker/me`),
  })
}

export function useUploadSpeakerFile(slug: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (file: File) =>
      apiUpload(
        `${base(slug)}/speaker/assets?filename=${encodeURIComponent(file.name)}`,
        file,
        file.type || 'application/octet-stream',
      ),
    onSuccess: async () => client.invalidateQueries({ queryKey: ['events', slug, 'speaker'] }),
  })
}

/** Generic owner-only list/mutation helpers for the operations console. */
export function useOwnerList<T>(slug: string, path: string, enabled: boolean): UseQueryResult<T[]> {
  return useQuery({
    queryKey: ['events', slug, 'owner', path],
    enabled,
    queryFn: () => apiRequest<T[]>(`${base(slug)}${path}`),
  })
}

export function useOwnerAction(slug: string, invalidatePath: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (v: { path: string; method: 'POST' | 'PATCH'; body?: unknown }) =>
      apiRequest<unknown>(`${base(slug)}${v.path}`, {
        method: v.method,
        ...(v.body === undefined ? {} : { body: v.body }),
      }),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: ['events', slug, 'owner', invalidatePath] }),
  })
}

export interface EmailPreview {
  audience: string
  recipientCount: number
  alreadyDelivered: number
  sample: string[]
  confirmToken: string
}

export interface ManifestRow {
  id: string
  status: 'confirmed' | 'waitlisted'
  displayName: string
  organization: string | null
  jobTitle: string | null
  checkedIn: boolean
}

export function useManifest(
  slug: string,
  slotCode: string,
  enabled: boolean,
): UseQueryResult<ManifestRow[]> {
  return useQuery({
    queryKey: ['events', slug, 'manifest', slotCode],
    enabled: enabled && slotCode !== '',
    queryFn: () =>
      apiRequest<ManifestRow[]>(`${base(slug)}/staff/manifest/${encodeURIComponent(slotCode)}`),
  })
}

export interface RiderContact {
  realName: string
  organization: string | null
  jobTitle: string | null
  status: string
  mobile: string | null
}

/** A mutation, not a query: every load is an audited access, so it only ever runs on an explicit click. */
export function useLoadContacts(slug: string) {
  return useMutation({
    mutationFn: (slotCode: string) =>
      apiRequest<RiderContact[]>(
        `${base(slug)}/staff/manifest/${encodeURIComponent(slotCode)}/contacts`,
      ),
  })
}
